<script lang="ts">
	import Uppy, { type UploadResult, type Meta } from "@uppy/core"
	import Dashboard from "@uppy/svelte/dashboard"

	import "@uppy/core/css/style.min.css"
	import "@uppy/dashboard/css/style.min.css"

	import Tus from "@uppy/tus"
	import { onDestroy } from "svelte"
	import { UploadError } from "../model/errors"

	interface Props {
		endpoint: string
		uploadType: "media" | "scene"
		allowedMimeTypes: Array<string>
		userId: number
		folderId: number
		deviceId?: number
		entryId?: number
		onUploadComplete: () => void
		onAllComplete: (res: UploadResult<Meta, Record<string, never>>) => void
		onStart: () => void
		onError: (err: UploadError) => void
	}

	let nonce = Date.now()
	let {
		endpoint,
		uploadType,
		entryId,
		folderId,
		deviceId,
		userId,
		allowedMimeTypes,
		onUploadComplete,
		onAllComplete,
		onStart,
		onError,
	}: Props = $props()

	const META_FIELDS = [
		"upload_type",
		"folder_id",
		"type",
		"user_id",
		"name",
		"entry_id",
		"device_id",
	] as const

	type MetaField = (typeof META_FIELDS)[number]
	type MetaDict = {
		[key in MetaField]?: any
	}

	/**
	 * Pick me up pick me up pick me up
	 */
	function giveMeUppies(deviceId: number = undefined, entryId: number = undefined) {
		let metafields = ["upload_type", "folder_id", "type", "user_id", "name"]

		const meta: MetaDict = {
			folder_id: folderId,
			upload_type: uploadType,
			user_id: userId,
		}

		if (deviceId != undefined) {
			metafields.push("device_id")
			meta.device_id = deviceId
		}
		if (entryId != undefined) {
			metafields.push("entry_id")
			meta.entry_id = entryId
		}

		const uppy = new Uppy({
			autoProceed: false,
			restrictions: {
				allowedFileTypes: allowedMimeTypes,
			},
		}).use(Tus, {
			endpoint,
			allowedMetaFields: [...metafields],
			removeFingerprintOnSuccess: true,
			chunkSize: 128 * 1024 * 1024,
			limit: 4,
			onSuccess: onUploadComplete,
		})

		uppy.setMeta(meta)

		uppy.on("complete", onAllComplete)
		uppy.on("upload-start", onStart)

		uppy.on("upload-error", (file, error) => {
			const [code, message] = getTusStatusAndMessage(error)
			console.log({ code, message })
			onError(new UploadError(code, message))
		})

		return uppy
	}

	function getTusStatusAndMessage(error: any): [number | null, string | null] {
		// Structured status from XHR snapshots
		const sentryStatus =
			error?.originalResponse?._xhr?.__sentry_xhr_v3__?.status_code ??
			error?.originalRequest?._xhr?.__sentry_xhr_v3__?.status_code
		const structuredStatus = typeof sentryStatus === "number" ? sentryStatus : null

		// Structured body if present
		const structuredBody =
			error?.originalResponse?.getBody?.() ??
			error?.originalResponse?._xhr?.__sentry_xhr_v3__?.body ??
			null

		// Fallback: parse from tus error message text
		const msgText = error?.message || ""
		const match = msgText.match(/response code:\s*(\d{3})/i)
		const parsedStatus = match ? Number(match[1]) : null
		const parsedBodyMatch = msgText.match(/response text:\s*([^,]+)(?:,|$)/i)
		const parsedBody = parsedBodyMatch ? parsedBodyMatch[1].trim() : null

		const status = structuredStatus ?? parsedStatus
		const message = structuredBody ?? parsedBody ?? null
		return [status, message]
	}

	let uppy = $derived(giveMeUppies(deviceId, entryId))

	onDestroy(() => {
		uppy.cancelAll()
	})
</script>

<div id={`file-uploader-${nonce}`} class="file-uploader">
	<Dashboard {uppy} props={{ width: "100%", height: "100%", inline: true }} />
</div>

<style>
	.file-uploader {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
	}

	.file-uploader :global(.uppy-Container),
	.file-uploader :global(.uppy-Root),
	.file-uploader :global(.uppy-Dashboard) {
		height: 100%;
		width: 100%;
	}

	.file-uploader :global(.uppy-Dashboard-dropFilesHereHint) {
		border-radius: 1rem;
	}

	.file-uploader :global(.uppy-Dashboard-inner) {
		background-color: var(--color-bg);
		border: none;
	}

	.file-uploader :global(.uppy-Dashboard-Item-name),
	.file-uploader :global(.uppy-DashboardContent-title),
	.file-uploader :global(.uppy-Dashboard-AddFiles-title) {
		color: var(--color-text-light);
	}

	.file-uploader :global(.uppy-Dashboard-AddFiles) {
		border: none;
	}

	.file-uploader :global(.uppy-Dashboard-AddFiles-info) {
		display: none;
	}

	.file-uploader :global(.uppy-Container button) {
		transform: none;
	}

	.file-uploader :global(.uppy-DashboardContent-bar) {
		padding: 0.25rem 1.2rem 0 1.2rem;
		background-color: var(--color-bg);
		border: none;
	}

	.file-uploader :global(.uppy-DashboardContent-bar button) {
		background-color: transparent;
		border: 2px solid var(--color-main);
		color: var(--color-text-light);
		border-radius: 99px;
		padding: 0.5rem 1rem;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.file-uploader :global(.uppy-DashboardContent-addMoreCaption) {
		margin-left: 0.25rem;
	}

	.file-uploader :global(.uppy-DashboardContent-bar button:hover),
	.file-uploader :global(.uppy-DashboardContent-bar button:focus-visible) {
		background-color: var(--color-main);
	}

	.file-uploader :global(.uppy-StatusBar) {
		background-color: transparent;
		border: none;
	}

	.file-uploader :global(.uppy-StatusBar-actions) {
		background-color: transparent;
		border: none;
		justify-content: flex-end;
	}

	.file-uploader :global(.uppy-StatusBar:before) {
		content: unset;
	}

	.file-uploader :global(.uppy-StatusBar-actions button) {
		border-radius: 99px;
	}

	.file-uploader :global(.uppy-StatusBar-actionBtn--upload) {
		background-color: var(--color-main);
	}

	.file-uploader :global(.uppy-DashboardContent-addMore) {
		width: unset;
		height: unset;
	}

	.file-uploader :global(.uppy-Dashboard-browse) {
		color: var(--color-main);
		border-color: var(--color-main);
	}

	.file-uploader :global(.uppy-c-icon:not(.uppy-StatusBar-statusIndicator)) {
		margin-inline-end: 0;
	}

	.file-uploader :global(.uppy-Dashboard-AddFilesPanel) {
		background: var(--color-bg-darker);
	}

	.file-uploader :global(.uppy-StatusBar-spinner) {
		fill: var(--color-main);
	}

	.file-uploader :global(.uppy-StatusBar-progress) {
		background-color: var(--color-main);
	}

	.file-uploader :global(.uppy-StatusBar-status) {
		color: var(--color-text-light);
	}

	.file-uploader :global(.uppy-StatusBar-statusSecondary) {
		color: var(--color-text);
	}
</style>
